import React from 'react';
import Button from '../Button';
import { InfoIcon } from '../icons';
import { HelpTrigger } from '../../help';
import { formatDate } from '../../utils/dateUtils';
import type { IncomingRoster } from './incomingRoster';

interface IncomingRosterNoticeProps {
  incoming: IncomingRoster;
  /** The page is showing the incoming session (else: the current one). */
  viewingIncoming: boolean;
  onViewIncoming: () => void;
}

const undecidedText = (incoming: IncomingRoster) => {
  const parts = (['House', 'Senate'] as const)
    .map((chamber) => ({ chamber, n: incoming.chambers[chamber]?.undecided ?? 0 }))
    .filter(({ n }) => n > 0)
    .map(({ chamber, n }) => `${n} ${chamber} seat${n === 1 ? '' : 's'}`);
  return parts.length ? `${parts.join(' and ')} still awaiting results.` : '';
};

/**
 * Between results day and convene day (ELECTION_OUTCOMES_PRD.md §5.14): on the
 * current session, a pointer to the incoming roster; on the incoming one, when its
 * members take office and which seats are still undecided.
 */
const IncomingRosterNotice: React.FC<IncomingRosterNoticeProps> = ({ incoming, viewingIncoming, onViewIncoming }) => (
  <div className="card flex flex-wrap items-start gap-3 p-4">
    <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary-600 dark:text-primary-400" aria-hidden />
    <div className="min-w-0 flex-1 text-sm text-text-secondary dark:text-text-dark-secondary">
      {viewingIncoming ? (
        <>
          <p className="font-medium text-text-primary dark:text-text-dark-primary">
            Incoming General Assembly: {incoming.label}
          </p>
          <p>
            Members-elect take office when the session convenes
            {incoming.conveneDate ? ` (${formatDate(incoming.conveneDate)})` : ''}. Tracking a member-elect now
            carries into the session automatically. {undecidedText(incoming)}
          </p>
        </>
      ) : (
        <p>
          <span className="font-medium text-text-primary dark:text-text-dark-primary">
            {incoming.shortLabel} members-elect are in.
          </span>{' '}
          See who won, who continues, and start tracking them before the session begins.
        </p>
      )}
    </div>
    <div className="flex items-center gap-1">
      {!viewingIncoming && (
        <Button variant="outline" size="sm" onClick={onViewIncoming}>View Incoming</Button>
      )}
      <HelpTrigger contentKey="incoming-legislators" size="sm" ariaLabel="About members-elect and the incoming session" />
    </div>
  </div>
);

export default IncomingRosterNotice;
